import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Page } from '@/shell/AppShell'
import { ForgeButton, IconButton } from '@/components/forge/ForgeButton'
import { ForgeDialog } from '@/components/forge/ForgeDialog'
import { HierarchyTrail } from '@/components/forge/HierarchyTrail'
import { Icon } from '@/components/forge/Icon'
import { Panel } from '@/components/forge/Panel'
import { EmptyState, ErrorState, SkeletonRows } from '@/components/forge/States'
import { Dot, Eyebrow, Mono, Num } from '@/components/forge/primitives'
import { queryKeys } from '@/services/queryClient'
import { chartToCsv, PLOT_MODES } from '@/features/signals/charts'
import {
  CHART_WINDOWS,
  DEFAULT_LIVE_WINDOW,
  DEFAULT_WINDOW,
  LIVE_WINDOWS,
  LIVE_WINDOW_MS,
  MAX_TAGS_PER_CHART,
  resolveSpan,
} from '@/features/sensorCharts/api'
import { useSensorChartLive } from '@/features/sensorCharts/live'
import {
  useDeleteSensorChart,
  useLocationSensorTree,
  useSensorChart,
  useSensorChartAccess,
  useSensorChartReadings,
  useUpdateSensorChart,
} from '@/features/sensorCharts/queries'
import { pickTags, tagLabel } from '@/features/sensorCharts/tree'
import { toPlot } from '@/features/sensorCharts/plot'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useToast } from '@/providers/ToastProvider'
import { formatDateTime, formatRelative } from '@/lib/format'
// import { DateRangePicker } from './DateRangePicker'
import { ChartSkeleton, ChartWriteError, SensorBrowser } from './SensorChartParts'
import { SensorTagTree } from './SensorTagTree'
import { WorkstationChart } from './WorkstationChart'

/**
 * One saved sensor chart. LIVE.
 *
 * The layout, the plot, the plot modes, the span controls and the URL contract
 * are the ad-hoc Workstation's (`WorkstationPage`): `?window=` or `?from=&to=`,
 * and `?mode=`. What the Workstation shows and this does not — statistics,
 * correlations, the AI insight, threshold rules, granularity, history — is
 * computed there from authored fixtures, and the API serves none of it for a
 * real chart, so it is left out rather than imitated.
 *
 * ── The URL is the VIEW, the server is the CHART ─────────────────────────
 * Unlike the ad-hoc screen, adding or removing a tag here is a write
 * (`PUT /sensors/charts/{id}`, a full replace of name + tags), not a `?signals=`
 * override. The chart's tag set is what the server says it is, always.
 *
 * ── One write at a time ───────────────────────────────────────────────────
 * Every PUT is "the current set ± something". Two in flight would each be
 * computed from the same stale set, and the second would undo the first. So
 * every tag control is disabled while a write is pending; a failed write leaves
 * the server's set — and therefore the screen — exactly as it was.
 */
export function SensorChartPage() {
  const { t } = useTranslation(['app', 'common'])
  const { chartId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const client = useQueryClient()
  const { toast } = useToast()

  const access = useSensorChartAccess()
  const chart = useSensorChart(access.charts ? chartId : null)
  const update = useUpdateSensorChart()
  const remove = useDeleteSensorChart()
  const [adding, setAdding] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)

  const data = chart.data
  useDocumentTitle(`${data?.name ?? t('sensors.charts.title')} · ${t('common:brand.name')}`)

  /* ── The view, from the URL ── */
  const windowKey = CHART_WINDOWS.includes(searchParams.get('window')) ? searchParams.get('window') : DEFAULT_WINDOW
  const from = searchParams.get('from')
  const to = searchParams.get('to')
  const range = useMemo(() => (from && to ? { from, to } : null), [from, to])
  const spanInput = useMemo(() => (range ? range : { window: windowKey }), [range, windowKey])

  const setParams = (mutate) =>
    setSearchParams((current) => {
      const url = new URLSearchParams(current)
      mutate(url)
      return url
    })
  const pickWindow = (next) =>
    setParams((url) => {
      url.set('window', next)
      url.delete('from')
      url.delete('to')
    })
  /* The date range picker was removed from this view 2026-10-14, by request.
     `from`/`to` are still read above, so a link that carries them still opens
     on that range; picking a window clears them.

  const pickRange = (next) =>
    setParams((url) => {
      if (next) {
        url.set('from', next.from)
        url.set('to', next.to)
        url.delete('window')
      } else {
        url.delete('from')
        url.delete('to')
      }
    }) */

  /* ── Where each tag lives, for labels and the signal tree ── */
  const browser = useLocationSensorTree(data?.place, { enabled: access.browse && Boolean(data?.place) })
  const tagIds = useMemo(() => (data?.sensorTags ?? []).map((tag) => tag.id), [data])
  const tagSet = useMemo(() => new Set(tagIds), [tagIds])
  const signalTree = useMemo(() => pickTags(browser.tree, tagSet), [browser.tree, tagSet])
  /* Tags on the chart that the browser could not place — a sensor outside the
     reader's sensor grant, or no sensor grant at all. Still listed, by the
     chart's own name for them, and still removable. Never silently dropped. */
  const unplaced = useMemo(
    () => (data?.sensorTags ?? []).filter((tag) => !browser.index.has(tag.id)),
    [data, browser.index],
  )

  const series = useMemo(
    () =>
      (data?.sensorTags ?? []).map((tag) => {
        const placed = browser.index.get(tag.id)
        const unit = placed?.tag.valueUnitId ? browser.unitSymbol(placed.tag.valueUnitId) : ''
        const label = tagLabel(placed?.tag ?? tag, t)
        return { id: tag.id, name: placed ? `${placed.sensor.name} · ${label}` : label, unit }
      }),
    [data, browser.index, browser.unitSymbol, t], // eslint-disable-line react-hooks/exhaustive-deps
  )

  /* History or Live. One URL parameter, and only the chosen source is
     connected: a live socket left open under the history view would spend one of
     the user's twenty stream slots on a trace nobody is looking at. */
  const view = searchParams.get('view') === 'live' ? 'live' : 'history'
  const liveKey = LIVE_WINDOWS.includes(searchParams.get('live')) ? searchParams.get('live') : DEFAULT_LIVE_WINDOW
  const pickView = (next) =>
    setParams((url) => {
      if (next === 'live') url.set('view', 'live')
      else url.delete('view')
    })

  const readings = useSensorChartReadings(data?.id, spanInput, tagIds, {
    enabled: Boolean(data) && view === 'history',
  })

  const live = useSensorChartLive(data?.id, tagIds, LIVE_WINDOW_MS[liveKey], {
    enabled: Boolean(data) && view === 'live',
  })
  /**
   * The span the history chart is DRAWN over — the one asked for, at once.
   *
   * While a step's readings are in flight the hook still holds the previous
   * span's. Drawing those against the new span, clipped to it, is what makes a
   * step look like a step: the half both spans share slides across, and the
   * other half fills in when the request lands, instead of the whole chart
   * dropping to a skeleton and coming back.
   */
  const shownSpan = useMemo(
    () => (readings.isPlaceholderData ? resolveSpan(spanInput) : readings.span),
    [readings.isPlaceholderData, readings.span, spanInput],
  )
  const shownReadings = useMemo(() => {
    if (!readings.isPlaceholderData || !shownSpan) return readings.readings
    return readings.readings.filter((reading) => {
      const at = Date.parse(reading.at)
      return at >= shownSpan.fromMs && at <= shownSpan.toMs
    })
  }, [readings.isPlaceholderData, readings.readings, shownSpan])

  /**
   * Earlier / Later — the anomaly trace's arrows, over the server's time span.
   *
   * The anomaly chart pans within readings it already holds; this one reads a
   * span from the server, so a step moves the SPAN: `?from=&to=` slide by half
   * its length, the same half-a-screen step `TraceChart` takes so the before and
   * the after share context. `?window=` is left alone, so a step forward that
   * would reach now drops the range and the view is the rolling window again —
   * there is nothing later than now to step to.
   */
  const pan = (direction) => {
    const current = shownSpan ?? resolveSpan(spanInput)
    const step = Math.max(Math.round((current.toMs - current.fromMs) / 2), 1000)
    const nextFrom = current.fromMs + direction * step
    const nextTo = current.toMs + direction * step
    setParams((url) => {
      if (direction > 0 && nextTo >= Date.now()) {
        url.delete('from')
        url.delete('to')
        return
      }
      url.set('from', new Date(nextFrom).toISOString())
      url.set('to', new Date(nextTo).toISOString())
    })
  }

  const source = view === 'live' ? live : { readings: shownReadings, span: shownSpan }
  const plot = useMemo(
    () =>
      data && source.span
        ? toPlot({ name: data.name, series, readings: source.readings, span: source.span })
        : null,
    [data, series, source.readings, source.span],
  )

  /* Three or more units cannot share two axes honestly, so such a chart opens
     stacked unless the URL already says otherwise. */
  const modeParam = searchParams.get('mode')
  const mode = PLOT_MODES.includes(modeParam) ? modeParam : plot?.unitCount > 2 ? 'stacked' : 'overlay'

  /* ── Writes ── */
  const writeTags = async (nextIds, message) => {
    if (update.isPending || !data) return
    try {
      await update.mutateAsync({ id: data.id, name: data.name, sensorTagIds: nextIds })
      toast(message, 'ok')
      setAdding(false)
    } catch {
      // Shown from `update.error`; the chart keeps the server's set.
    }
  }

  const removeTag = (tagId) => {
    const tag = data.sensorTags.find((entry) => entry.id === tagId)
    writeTags(
      tagIds.filter((id) => id !== tagId),
      t('sensors.charts.removedToast', { tag: tagLabel(browser.index.get(tagId)?.tag ?? tag, t) }),
    )
  }

  const deleteChart = async () => {
    try {
      await remove.mutateAsync(data.id)
      toast(t('sensors.charts.deletedToast', { name: data.name }), 'ok')
      navigate('/app/sensors', { replace: true })
      /* Dropped once the screen has gone, so nothing refetches a chart that no
         longer exists — see `useDeleteSensorChart`. */
      client.removeQueries({ queryKey: queryKeys.sensorChart(data.id) })
      client.removeQueries({ queryKey: [...queryKeys.sensorCharts, 'readings', data.id] })
    } catch {
      // Shown in the dialog from `remove.error`.
    }
  }

  const exportCsv = () => {
    const csv = chartToCsv(plot, {})
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `${slug(data.name)}.csv`
    anchor.click()
    URL.revokeObjectURL(url)
    toast(t('sensors.workstation.exportedCsv'), 'ok')
  }

  /* The window row — pills, Earlier / Later and the span read — rendered in the
     toolbar and again in the full-screen dialog. Everything it sets is in the
     URL, so two copies cannot disagree. */
  const spanRow = view === 'history' ? (
    <div className="flex flex-wrap items-center gap-2 px-3.5 py-2">
      <div
        role="group"
        aria-label={t('sensors.window.label')}
        className="flex flex-wrap gap-[3px] rounded-md bg-sunken p-[3px]"
      >
        {CHART_WINDOWS.map((entry) => (
          <button
            key={entry}
            type="button"
            aria-pressed={!range && windowKey === entry}
            onClick={() => pickWindow(entry)}
            className={
              !range && windowKey === entry
                ? 'rounded-xs bg-forge px-3 py-1 text-body-sm font-semibold text-white'
                : 'rounded-xs px-3 py-1 text-body-sm text-ink-dim transition-colors hover:text-ink'
            }
          >
            {t(`sensors.window.${entry}`)}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-1">
        <IconButton bare label={t('sensors.charts.panBack')} onClick={() => pan(-1)}>
          <Icon name="right" size="sm" className="rotate-180 mirror-rtl" />
        </IconButton>
        {/* Quiet on a rolling window: it already ends now. */}
        <IconButton bare disabled={!range} label={t('sensors.charts.panForward')} onClick={() => pan(1)}>
          <Icon name="right" size="sm" className="mirror-rtl" />
        </IconButton>
      </div>

      {shownSpan ? (
        <Mono className="text-micro text-ink-dim">
          {formatDateTime(new Date(shownSpan.fromMs).toISOString())} →{' '}
          {formatDateTime(new Date(shownSpan.toMs).toISOString())}
        </Mono>
      ) : null}

      <p className="m-0 ms-auto flex items-center gap-1.5 text-micro font-normal text-ink-dim">
        {readings.hasMore ? (
          <>
            <Dot tone="amber" size={6} />
            <Num>
              {t('sensors.charts.partial', {
                when: readings.loadedTo ? formatDateTime(readings.loadedTo.replace(' ', 'T') + 'Z') : '—',
              })}
            </Num>
            <ForgeButton size="sm" onClick={() => readings.loadMore()} disabled={readings.loadingMore}>
              {readings.loadingMore ? t('sensors.charts.loadingMore') : t('sensors.charts.loadMore')}
            </ForgeButton>
          </>
        ) : readings.span ? (
          <Num>{t('sensors.charts.fullSpan')}</Num>
        ) : null}
      </p>
    </div>
  ) : (
    <div className="flex flex-wrap items-center gap-2 px-3.5 py-2">
      <div
        role="group"
        aria-label={t('sensors.charts.liveWindowLabel')}
        className="flex gap-[3px] rounded-md bg-sunken p-[3px]"
      >
        {LIVE_WINDOWS.map((entry) => (
          <button
            key={entry}
            type="button"
            aria-pressed={liveKey === entry}
            onClick={() => setParams((url) => url.set('live', entry))}
            className={
              liveKey === entry
                ? 'rounded-xs bg-forge px-3 py-1 text-body-sm font-semibold text-white'
                : 'rounded-xs px-3 py-1 text-body-sm text-ink-dim transition-colors hover:text-ink'
            }
          >
            {t(`sensors.charts.liveWindow.${entry}`)}
          </button>
        ))}
      </div>
      <p className="m-0 ms-auto flex items-center gap-1.5 text-micro font-normal text-ink-dim">
        <Dot tone={LIVE_TONE[live.status] ?? 'amber'} size={6} />
        <Num>
          {t(`sensors.charts.liveStatus.${live.available ? live.status : 'unavailable'}`, {
            readings: live.readings.length,
          })}
        </Num>
      </p>
    </div>
  )

  const chartBody = tagIds.length === 0 ? (
    <EmptyState
      icon="chart"
      title={t('sensors.charts.noTagsOnChart')}
      body={t('sensors.charts.noTagsOnChartBody')}
      className="border-0 shadow-none"
    />
  ) : view === 'live' ? (
    live.status === 'failed' ? (
      <EmptyState
        icon="wave"
        title={t('sensors.charts.liveFailed')}
        body={t('sensors.charts.liveFailedBody')}
        className="border-0 shadow-none"
      />
    ) : !plot || plot.stamps.length === 0 ? (
      <EmptyState
        icon="wave"
        title={t('sensors.charts.liveWaiting')}
        body={t('sensors.charts.liveWaitingBody')}
        className="border-0 shadow-none"
      />
    ) : (
      <WorkstationChart chart={plot} visible={{}} mode={mode} fill={fullscreen} />
    )
  ) : readings.isError ? (
    <ErrorState
      error={readings.error}
      title={t('sensors.charts.readingsFailed')}
      onRetry={readings.reset}
      className="border-0 shadow-none"
    />
  ) : readings.isPending || !plot || (readings.isPlaceholderData && plot.stamps.length === 0) ? (
    /* The last clause: a step with nothing shared to slide (back to
       now from far away) waits, rather than flashing "no readings". */
    <ChartSkeleton fill={fullscreen} />
  ) : plot.stamps.length === 0 ? (
    <EmptyState
      icon="wave"
      title={t('sensors.charts.noReadings')}
      body={t('sensors.charts.noReadingsBody')}
      className="border-0 shadow-none"
    />
  ) : (
    <WorkstationChart chart={plot} visible={{}} mode={mode} fill={fullscreen} />
  )

  if (access.isPending) {
    return (
      <Page>
        <SkeletonRows count={5} />
      </Page>
    )
  }

  return (
    <Page>
      <header className="mb-3.5">
        <p className="m-0 mb-1.5">
          <Link
            to="/app/sensors"
            className="inline-flex items-center gap-1.5 text-body-sm text-ink-dim transition-colors hover:text-ink"
          >
            <Icon name="right" size="xs" className="rotate-180 mirror-rtl" />
            {t('sensors.workstation.allCharts')}
          </Link>
        </p>
        {data ? (
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="m-0 text-display text-balance">{data.name}</h1>
              <HierarchyTrail
                hierarchy={data.hierarchy}
                emptyLabel={t('sensors.charts.placeGone')}
                className="mt-1.5"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {data.createdAt ? (
                <Num className="text-micro font-normal text-ink-dim">
                  {t('sensors.charts.createdWhen', { when: formatRelative(data.createdAt) })}
                </Num>
              ) : null}
              <ForgeButton variant="danger" onClick={() => setDeleting(true)}>
                <Icon name="trash" size="xs" />
                {t('sensors.charts.deleteChart')}
              </ForgeButton>
            </div>
          </div>
        ) : null}
      </header>

      {!access.charts ? (
        <EmptyState icon="lock" title={t('sensors.charts.noAccess')} body={t('sensors.charts.noAccessBody')} />
      ) : chart.isError ? (
        <ErrorState
          error={chart.error}
          title={chart.error?.code === 'notFound' ? t('sensors.charts.notFound') : t('sensors.charts.loadFailed')}
          onRetry={chart.error?.code === 'notFound' ? undefined : () => chart.refetch()}
        />
      ) : !data ? (
        <SkeletonRows count={5} />
      ) : (
        <>
          {/* Toolbar: plot mode and span above, the window pills and the span
              actually read below — the Workstation's two rows. */}
          <Panel flush className="mb-3.5">
            <div className="flex flex-wrap items-center gap-2 border-b border-hairline px-3.5 py-2.5">
              <div
                role="group"
                aria-label={t('sensors.charts.viewLabel')}
                className="flex gap-[3px] rounded-md bg-sunken p-[3px]"
              >
                {['history', 'live'].map((entry) => (
                  <button
                    key={entry}
                    type="button"
                    aria-pressed={view === entry}
                    onClick={() => pickView(entry)}
                    className={
                      view === entry
                        ? 'flex items-center gap-1.5 rounded-xs bg-forge px-3 py-1 text-body-sm font-semibold text-white'
                        : 'flex items-center gap-1.5 rounded-xs px-3 py-1 text-body-sm text-ink-dim transition-colors hover:text-ink'
                    }
                  >
                    <Icon name={entry === 'live' ? 'wave' : 'cal'} size="xs" />
                    {t(`sensors.charts.view.${entry}`)}
                  </button>
                ))}
              </div>

              <div
                role="group"
                aria-label={t('sensors.workstation.modeLabel')}
                className="flex gap-[3px] rounded-md bg-sunken p-[3px]"
              >
                {PLOT_MODES.map((entry) => (
                  <button
                    key={entry}
                    type="button"
                    aria-pressed={mode === entry}
                    onClick={() => setParams((url) => url.set('mode', entry))}
                    className={
                      mode === entry
                        ? 'rounded-xs bg-forge px-3 py-1 text-body-sm font-semibold text-white'
                        : 'rounded-xs px-3 py-1 text-body-sm text-ink-dim transition-colors hover:text-ink'
                    }
                  >
                    {t(`sensors.workstation.mode.${entry}`)}
                  </button>
                ))}
              </div>

              {/* Date range picker removed 2026-10-14 — see `pickRange`.
              {view === 'history' ? <DateRangePicker value={range} onChange={pickRange} /> : null} */}

              <span className="ms-auto flex flex-wrap gap-2">
                {view === 'history' ? (
                  <ForgeButton size="sm" onClick={readings.reset} disabled={readings.isPending}>
                    <Icon name="rail" size="xs" />
                    {t('sensors.charts.refresh')}
                  </ForgeButton>
                ) : null}
                <ForgeButton size="sm" onClick={exportCsv} disabled={!plot?.stamps.length}>
                  <Icon name="doc" size="xs" />
                  {t('sensors.charts.exportCsv')}
                </ForgeButton>
                <ForgeButton size="sm" onClick={() => setFullscreen(true)} disabled={tagIds.length === 0}>
                  <Icon name="layers" size="xs" />
                  {t('sensors.charts.fullscreen')}
                </ForgeButton>
              </span>
            </div>

            {spanRow}
          </Panel>

          <Panel className="mb-3.5">
            {/* One of the two renders at a time, as on the anomaly live card: the
                chart is a single element and mounting it in both places would
                draw it twice, once invisibly behind the dialog. */}
            {fullscreen ? null : chartBody}
          </Panel>

          {/* The chart's signals, in the same tree the create screen used. */}
          <Panel>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Eyebrow className="flex-1">
                {t('sensors.charts.signals')} ·{' '}
                <Num>{t('sensors.charts.capacity', { count: tagIds.length, max: MAX_TAGS_PER_CHART })}</Num>
              </Eyebrow>
              <ForgeButton
                variant="primary"
                size="sm"
                onClick={() => {
                  update.reset()
                  setAdding(true)
                }}
                disabled={update.isPending || tagIds.length >= MAX_TAGS_PER_CHART || !data.place}
              >
                <Icon name="plus" size="xs" />
                {t('sensors.charts.addSignals')}
              </ForgeButton>
            </div>

            {!adding ? <ChartWriteError error={update.error} action="update" className="mb-3" /> : null}

            {tagIds.length === 0 ? (
              <p className="m-0 rounded-sm border border-dashed border-hairline px-3.5 py-6 text-center text-body-sm text-ink-dim">
                {t('sensors.charts.noneSelected')}
              </p>
            ) : (
              <div className="flex flex-col gap-3">
                {access.browse && browser.isPending ? <SkeletonRows count={2} /> : null}
                {signalTree ? (
                  <div className="overflow-hidden rounded-sm border border-hairline">
                    <SensorTagTree
                      key={signalTree.key}
                      tree={signalTree}
                      mode="review"
                      unitSymbol={browser.unitSymbol}
                      onRemove={removeTag}
                      disabled={update.isPending}
                    />
                  </div>
                ) : null}
                {unplaced.length && !browser.isPending ? (
                  <div>
                    <p className="m-0 mb-1.5 text-micro text-ink-mute">
                      {access.browse ? t('sensors.charts.unplaced') : t('sensors.charts.unplacedNoBrowse')}
                    </p>
                    <ul className="m-0 list-none overflow-hidden rounded-sm border border-hairline p-0">
                      {unplaced.map((tag) => (
                        <li key={tag.id} className="flex items-center gap-2.5 border-b border-subtle px-3 py-2 last:border-b-0">
                          <Icon name="wave" size="xs" className="shrink-0 text-ink-mute" />
                          <span className="min-w-0 flex-1 truncate text-body-sm text-ink">{tagLabel(tag, t)}</span>
                          <button
                            type="button"
                            disabled={update.isPending}
                            onClick={() => removeTag(tag.id)}
                            aria-label={t('sensors.charts.tree.remove', { tag: tagLabel(tag, t) })}
                            className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-sm text-ink-mute transition-colors hover:bg-sunken hover:text-on-red disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <Icon name="x" size="sm" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            )}
          </Panel>
        </>
      )}

      {adding && data ? (
        <AddSignalsDialog
          chart={data}
          access={access}
          browser={browser}
          pending={update.isPending}
          error={update.error}
          onClose={() => setAdding(false)}
          onSubmit={(picked) =>
            writeTags([...tagIds, ...picked], t('sensors.charts.addedToast', { added: picked.length }))
          }
        />
      ) : null}

      {/* Full screen, as on the anomaly charts: a `full` dialog rather than the
          Fullscreen API, so the chart keeps the app's theme and a close people
          recognise. The window row comes with it, so the reader can keep
          stepping without leaving. The plot fills whatever height the row
          leaves (`fill`), as the anomaly trace does, rather than holding its
          aspect and leaving bands of empty stage. */}
      <ForgeDialog
        open={fullscreen && Boolean(data)}
        onOpenChange={(next) => (next ? null : setFullscreen(false))}
        size="full"
        title={data?.name ?? ''}
      >
        <div className="flex h-full min-h-0 flex-col gap-3">
          <div className="-mx-5 -mt-4 shrink-0 border-b border-hairline">{spanRow}</div>
          <div className="min-h-0 flex-1">{fullscreen ? chartBody : null}</div>
        </div>
      </ForgeDialog>

      {deleting && data ? (
        <ForgeDialog
          open
          size="sm"
          onOpenChange={(next) => {
            if (!next && !remove.isPending) {
              remove.reset()
              setDeleting(false)
            }
          }}
          title={t('sensors.charts.deleteTitle', { name: data.name })}
          footer={
            <>
              <ForgeButton variant="danger" disabled={remove.isPending} onClick={deleteChart}>
                {remove.isPending ? t('sensors.charts.deleting') : t('sensors.charts.deleteConfirm')}
              </ForgeButton>
              <ForgeButton
                disabled={remove.isPending}
                onClick={() => {
                  remove.reset()
                  setDeleting(false)
                }}
              >
                {t('common:actions.cancel')}
              </ForgeButton>
            </>
          }
        >
          <ChartWriteError error={remove.error} action="delete" className="mb-4" />
          <p className="m-0 text-body text-ink-dim">
            {t('sensors.charts.deleteBody', { tags: tagIds.length })}
          </p>
        </ForgeDialog>
      ) : null}
    </Page>
  )
}

/**
 * Add tags to an open chart.
 *
 * The browser is fixed to the CHART'S place: the server accepts only tags at
 * that place or beneath it, so offering the rest of the estate would offer
 * nothing but 400s. Tags already on the chart show ticked and locked.
 */
function AddSignalsDialog({ chart, access, browser, pending, error, onClose, onSubmit }) {
  const { t } = useTranslation(['app', 'common'])
  const [picked, setPicked] = useState(() => new Set())
  const locked = useMemo(() => new Set(chart.sensorTags.map((tag) => tag.id)), [chart])
  const room = MAX_TAGS_PER_CHART - locked.size

  const toggle = (tagId) =>
    setPicked((previous) => {
      const next = new Set(previous)
      if (next.has(tagId)) next.delete(tagId)
      else if (next.size < room && !locked.has(tagId)) next.add(tagId)
      return next
    })

  return (
    <ForgeDialog
      open
      size="lg"
      bodyClassName="max-h-[min(72vh,44rem)]"
      onOpenChange={(next) => !next && !pending && onClose()}
      title={t('sensors.charts.addTitle', { name: chart.name })}
      description={t('sensors.charts.addBody', { place: chart.place?.name ?? '—' })}
      footer={
        <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
          <Num className="me-auto text-micro font-normal text-ink-dim">
            {t('sensors.charts.capacity', { count: locked.size + picked.size, max: MAX_TAGS_PER_CHART })}
          </Num>
          <ForgeButton onClick={onClose} disabled={pending}>
            {t('common:actions.cancel')}
          </ForgeButton>
          <ForgeButton variant="primary" disabled={!picked.size || pending} onClick={() => onSubmit([...picked])}>
            {pending ? t('sensors.charts.saving') : t('sensors.charts.addConfirm', { added: picked.size })}
          </ForgeButton>
        </div>
      }
    >
      <ChartWriteError error={error} action="update" className="mb-3" />
      <SensorBrowser
        access={access}
        browser={browser}
        selected={picked}
        locked={locked}
        onToggle={toggle}
        atCapacity={picked.size >= room}
        disabled={pending}
      />
    </ForgeDialog>
  )
}

/** The status dot beside the live stream's state. */
const LIVE_TONE = { live: 'green', connecting: 'amber', retrying: 'amber', failed: 'red', idle: 'amber' }

/** A filename from a chart name. */
const slug = (name) =>
  (name ?? 'chart')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60) || 'chart'
